import React, { memo, useState } from 'react';
import { Handle } from '@xyflow/react';
import { 
  Box, 
  Typography, 
  useTheme, 
  alpha, 
  IconButton,
  Popover,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Base Node component that serves as a foundation for all neural network layer nodes
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Node data containing layer configuration
 * @param {string} props.id - Unique node ID
 * @param {boolean} props.selected - Whether the node is selected
 * @param {boolean} props.isConnectable - Whether the node can be connected
 * @param {Object} props.style - Additional style properties
 * @param {React.ReactNode} props.icon - Icon to display in the header
 * @param {React.ReactNode} props.controls - Node configuration controls
 * @param {string} props.nodeType - Type of node for styling
 * @param {React.ReactNode} props.preview - Visual representation of the node's function
 * @param {boolean} props.multipleInputs - Whether the node can have multiple input connections
 * @param {boolean} props.multipleOutputs - Whether the node can have multiple output connections
 * @param {boolean} props.disableInputHandle - Whether to disable the input handle
 * @param {boolean} props.disableOutputHandle - Whether to disable the output handle
 * @param {function} props.onDelete - Function to call when delete button is clicked
 */
const BaseNode = ({ 
  data, 
  id, 
  selected, 
  isConnectable,
  style = {},
  icon,
  controls,
  nodeType = 'default',
  preview,
  multipleInputs = false,
  multipleOutputs = false,
  disableInputHandle = false,
  disableOutputHandle = false,
  onDelete,
}) => {
  const theme = useTheme();
  const [editMode, setEditMode] = useState(false);
  const [editAnchorEl, setEditAnchorEl] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Get node color based on type, first from theme.nodeColors, then fallback to default
  const getNodeColor = () => {
    // Get the actual layer type (lowercase) from data if available
    const layerType = data.layerType ? data.layerType.toLowerCase() : nodeType;
    
    // If theme has nodeColors defined, use them
    if (theme.nodeColors) {
      // Try to match with the specific node type first
      if (theme.nodeColors[layerType]) {
        return theme.nodeColors[layerType];
      }
      
      // Fallback to node category
      if (nodeType && theme.nodeColors[nodeType]) {
        return theme.nodeColors[nodeType];
      }
    }
    
    // Fallback to theme primary color
    return theme.palette.primary.main;
  };
  
  // Get node text color based on type
  const getNodeTextColor = () => {
    const layerType = data.layerType ? data.layerType.toLowerCase() : nodeType;
    
    // If theme has nodeTextColors defined, use them
    if (theme.nodeTextColors) {
      // Try to match with the specific node type first
      if (theme.nodeTextColors[layerType]) {
        return theme.nodeTextColors[layerType];
      }
      
      // Fallback to node category
      if (nodeType && theme.nodeTextColors[nodeType]) {
        return theme.nodeTextColors[nodeType];
      }
    }
    
    // Fallback to white or black based on luminance
    return '#ffffff';
  };
  
  const nodeColor = getNodeColor();
  const nodeTextColor = getNodeTextColor();
  
  const handleEditClick = (event) => {
    event.stopPropagation();
    // For fixed nodes, no editing allowed
    if (data.isFixed) return;
    setEditAnchorEl(event.currentTarget);
    setEditMode(true);
  };
  
  const handleEditClose = () => {
    setEditAnchorEl(null);
    setEditMode(false);
  };
  
  const handleDeleteClick = (event) => {
    event.stopPropagation();
    // For fixed nodes, no deletion allowed
    if (data.isFixed) return;
    setConfirmDelete(true);
  };
  
  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete(id);
    }
    setConfirmDelete(false);
  };
  
  return (
    <Box
      sx={{
        padding: '10px',
        borderRadius: '6px',
        background: alpha(nodeColor, 0.1),
        border: `2px solid ${selected ? nodeColor : alpha(nodeColor, 0.6)}`,
        boxShadow: selected ? `0 0 0 2px ${alpha(nodeColor, 0.3)}` : 'none',
        width: '180px',
        transition: 'all 0.2s ease',
        position: 'relative',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
        },
        ...style,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 8px',
          backgroundColor: nodeColor,
          borderRadius: '4px',
          color: nodeTextColor,
          marginBottom: '8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon && (
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              {icon}
            </Box>
          )}
          <Typography
            variant="subtitle2"
            component="div"
            sx={{
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.label}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex' }}>
          {!data.isFixed && (
            <Tooltip title="Delete node">
              <IconButton 
                size="small" 
                onClick={handleDeleteClick}
                sx={{ 
                  color: nodeTextColor, 
                  p: 0.5,
                  mr: 0.5,
                  '&:hover': { bgcolor: alpha(nodeTextColor, 0.2) } 
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {!data.isFixed && (
            <Tooltip title="Edit node">
              <IconButton 
                size="small" 
                onClick={handleEditClick}
                sx={{ 
                  color: nodeTextColor, 
                  p: 0.5,
                  '&:hover': { bgcolor: alpha(nodeTextColor, 0.2) } 
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          minHeight: '60px',
        }}
      >
        {/* Node-specific content preview */}
        {preview}
      </Box>

      {/* Edit popover - only render if not a fixed node */}
      {!data.isFixed && (
        <Popover
          open={editMode}
          anchorEl={editAnchorEl}
          onClose={handleEditClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          sx={{ mt: 1 }}
        >
          <Box sx={{ p: 2, width: 300, maxHeight: 500, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              {`Configure ${data.label}`}
            </Typography>
            
            {/* Node-specific configuration controls */}
            {controls}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button size="small" onClick={handleEditClose}>
                Close
              </Button>
            </Box>
          </Box>
        </Popover>
      )}
      
      {/* Delete confirmation dialog - only render if not a fixed node */}
      {!data.isFixed && (
        <Dialog 
          open={confirmDelete} 
          onClose={() => setConfirmDelete(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Node</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this {data.label} node?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button 
              onClick={handleConfirmDelete} 
              color="error"
              variant="contained"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Input handle - only render if not disabled */}
      {!disableInputHandle && multipleInputs ? (
        // Multiple input handles (top, middle-left, bottom)
        <>
          <Handle
            type="target"
            position="top"
            id="input-top"
            isConnectable={isConnectable}
            style={{
              width: '10px',
              height: '10px',
              background: nodeColor,
              top: '0px',
              left: '50%',
            }}
          />
          <Handle
            type="target"
            position="left"
            id="input-left"
            isConnectable={isConnectable}
            style={{
              width: '10px',
              height: '10px',
              background: nodeColor,
              left: '-5px',
            }}
          />
          <Handle
            type="target"
            position="bottom"
            id="input-bottom"
            isConnectable={isConnectable}
            style={{
              width: '10px',
              height: '10px',
              background: nodeColor,
              bottom: '0px',
              left: '50%',
            }}
          />
        </>
      ) : !disableInputHandle ? (
        // Default single input handle
        <Handle
          type="target"
          position="left"
          id="input"
          isConnectable={isConnectable}
          style={{
            width: '12px',
            height: '12px',
            backgroundColor: nodeColor,
            border: `2px solid ${theme.palette.background.paper}`,
          }}
        />
      ) : null}
      
      {/* Output handle - only render if not disabled */}
      {!disableOutputHandle && multipleOutputs ? (
        // Multiple output handles
        <>
          <Handle
            type="source"
            position="right"
            id="output-right"
            isConnectable={isConnectable}
            style={{
              width: '10px',
              height: '10px',
              background: nodeColor,
              right: '-5px',
            }}
          />
          <Handle
            type="source"
            position="bottom"
            id="output-bottom"
            isConnectable={isConnectable}
            style={{
              width: '10px',
              height: '10px',
              background: nodeColor,
              bottom: '0px',
              left: '75%',
            }}
          />
        </>
      ) : !disableOutputHandle ? (
        // Default single output handle
        <Handle
          type="source"
          position="right"
          id="output"
          isConnectable={isConnectable}
          style={{
            width: '12px',
            height: '12px',
            backgroundColor: nodeColor,
            border: `2px solid ${theme.palette.background.paper}`,
          }}
        />
      ) : null}
    </Box>
  );
};

export default memo(BaseNode); 